import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Visibility,
  CloudUpload,
  Refresh,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import oneDriveService from '../services/oneDriveService';
import BulkImportProgress from '../components/BulkImportProgress';
import { useAuth } from '../../../hooks/useAuth';

const statusConfig = {
  pending:    { label: 'Pendiente',   color: 'default' },
  processing: { label: 'Procesando',  color: 'info' },
  paused:     { label: 'Pausado',     color: 'warning' },
  completed:  { label: 'Completado',  color: 'success' },
  failed:     { label: 'Fallido',     color: 'error' },
  cancelled:  { label: 'Cancelado',   color: 'default' },
};

const BulkImportHistoryPage = () => {
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    if (!isOwner) {
      navigate('/proceedings');
      return;
    }
    loadHistory();
  }, [isOwner]);

  const loadHistory = () => {
    setLoading(true);
    setError(null);
    oneDriveService
      .getJobHistory(50)
      .then(res => setJobs(res.data || []))
      .catch(() => setError('Error al cargar el historial de importaciones'))
      .finally(() => setLoading(false));
  };

  const handleDownloadReport = async (jobId, e) => {
    e.stopPropagation();
    try {
      const response = await oneDriveService.downloadReport(jobId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte-importacion-${jobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // ignore
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '—';

  return (
    <Box>
      <PageHeader
        title="Historial de Importaciones"
        subtitle="Cargues masivos desde OneDrive"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<Refresh />} onClick={loadHistory} disabled={loading}>
              Actualizar
            </Button>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/proceedings')}>
              Volver
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CloudUpload sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>No hay importaciones registradas</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Progreso</TableCell>
                  <TableCell align="center">Exitosos</TableCell>
                  <TableCell align="center">Fallidos</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map(job => {
                  const progress = job.totalFiles > 0
                    ? Math.round((job.processedFiles / job.totalFiles) * 100)
                    : 0;
                  const cfg = statusConfig[job.status] || statusConfig.pending;
                  const isActive = ['pending', 'processing', 'paused'].includes(job.status);

                  return (
                    <TableRow
                      key={job.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedJobId(job.id)}
                    >
                      <TableCell>
                        <Typography variant="body2">{formatDate(job.createdAt)}</Typography>
                        {job.completedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Fin: {formatDate(job.completedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={cfg.label} color={cfg.color} size="small" />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ flex: 1, height: 6, borderRadius: 1 }}
                            color={cfg.color === 'error' ? 'error' : cfg.color === 'success' ? 'success' : 'primary'}
                          />
                          <Typography variant="caption" sx={{ flexShrink: 0 }}>
                            {progress}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {job.processedFiles ?? 0} / {job.totalFiles ?? 0} archivos
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {job.successCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color={job.failedCount > 0 ? 'error.main' : 'text.secondary'} fontWeight={600}>
                          {job.failedCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{job.totalFiles ?? 0}</Typography>
                      </TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => setSelectedJobId(job.id)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(job.reportPath || job.status === 'completed') && (
                          <Tooltip title="Descargar reporte Excel">
                            <IconButton size="small" color="primary" onClick={e => handleDownloadReport(job.id, e)}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Job detail dialog */}
      <Dialog
        open={!!selectedJobId}
        onClose={() => setSelectedJobId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalle de Importación
          <IconButton
            size="small"
            onClick={() => setSelectedJobId(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedJobId && (
            <BulkImportProgress
              jobId={selectedJobId}
              onClose={() => setSelectedJobId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BulkImportHistoryPage;
